/**
 * Vulvi PDP gallery — Iter 2b
 * Desktop: vertical stack dots synced with scroll Y.
 * Mobile: horizontal swipe dots synced with scroll X.
 */
if (!window.customElements.get('vulvi-gallery-stack-dots')) {
  class VulviGalleryStackDots extends HTMLElement {
    connectedCallback() {
      this.#carousel = document.getElementById(this.getAttribute('aria-controls'));
      this.#buttons = Array.from(this.querySelectorAll('button'));

      if (!this.#carousel || this.#buttons.length === 0) {
        return;
      }

      this.#buttons.forEach((button, index) => {
        button.addEventListener('click', () => this.#scrollToIndex(index));
      });

      this.#carousel.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#carousel.addEventListener('carousel:filter', this.#onFilter);
      this.#layoutQuery = window.matchMedia('(max-width: 999px)');
      this.#layoutQuery.addEventListener('change', this.#onLayoutChange);
      this.#updateActiveIndex();
    }

    disconnectedCallback() {
      if (this.#carousel) {
        this.#carousel.removeEventListener('scroll', this.#onScroll);
        this.#carousel.removeEventListener('carousel:filter', this.#onFilter);
      }

      if (this.#layoutQuery) {
        this.#layoutQuery.removeEventListener('change', this.#onLayoutChange);
      }
    }

    #carousel = null;
    #buttons = [];
    #scrollFrame = null;
    #layoutQuery = null;

    #isHorizontalLayout() {
      return this.#layoutQuery?.matches ?? false;
    }

    #onScroll = () => {
      if (this.#scrollFrame) {
        return;
      }

      this.#scrollFrame = window.requestAnimationFrame(() => {
        this.#scrollFrame = null;
        this.#updateActiveIndex();
      });
    };

    #onFilter = () => {
      this.#buttons = Array.from(this.querySelectorAll('button:not([hidden])'));
      this.#updateActiveIndex();
    };

    #onLayoutChange = () => {
      this.#updateActiveIndex();
    };

    #visibleCells() {
      if (this.#carousel.cells) {
        return this.#carousel.cells;
      }

      return Array.from(this.#carousel.querySelectorAll('.product-gallery__media:not([hidden])'));
    }

    #scrollToIndex(index) {
      const cells = this.#visibleCells();
      const cell = cells[index];

      if (!cell) {
        return;
      }

      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

      if (this.#isHorizontalLayout()) {
        this.#carousel.scrollTo({
          left: cell.offsetLeft,
          behavior
        });
      } else {
        this.#carousel.scrollTo({
          top: cell.offsetTop,
          behavior
        });
      }

      this.#setActiveIndex(index);
    }

    #updateActiveIndex() {
      const cells = this.#visibleCells();

      if (cells.length === 0) {
        return;
      }

      const scrollPosition = this.#isHorizontalLayout() ? this.#carousel.scrollLeft : this.#carousel.scrollTop;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      cells.forEach((cell, index) => {
        const offset = this.#isHorizontalLayout() ? cell.offsetLeft : cell.offsetTop;
        const distance = Math.abs(offset - scrollPosition);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      this.#setActiveIndex(closestIndex);
    }

    #setActiveIndex(index) {
      this.#buttons.forEach((button, buttonIndex) => {
        button.setAttribute('aria-current', buttonIndex === index ? 'true' : 'false');
      });
    }
  }

  window.customElements.define('vulvi-gallery-stack-dots', VulviGalleryStackDots);
}
